import { useEffect, useState, useRef } from "react";
import { User, Building2, Mail, CreditCard, ShieldCheck, Loader2, LogOut, Phone, Hash, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import BillingHistory from "@/components/BillingHistory";
import { PdfBrandingPreview } from "@/components/PdfBrandingPreview";
import { useNavigate } from "react-router-dom";

export default function Account() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", company_phone: "", license_number: "" });
  const [saving, setSaving] = useState(false);
  const [hasLogo, setHasLogo] = useState(false);
  const [logoVersion, setLogoVersion] = useState(0);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user && typeof user === "object") {
      setForm({
        name: user.name || "",
        company_name: user.company_name || "",
        company_phone: user.company_phone || "",
        license_number: user.license_number || "",
      });
      setHasLogo(!!user.has_logo);
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      toast.success("Profile updated.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Logo must be under 3 MB.");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/account/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHasLogo(true);
      setLogoVersion((v) => v + 1);
      setUser((u) => (u && typeof u === "object" ? { ...u, has_logo: true } : u));
      toast.success("Logo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/account/logo");
      setHasLogo(false);
      setUser((u) => (u && typeof u === "object" ? { ...u, has_logo: false } : u));
      toast.success("Logo removed.");
    } catch {
      toast.error("Couldn't remove logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  if (!user || typeof user !== "object") return null;

  return (
    <div className="container-mtr py-8 sm:py-12 max-w-2xl" data-testid="account-page">
      <Seo title="Account — MeasureTheRoof" description="Manage your profile and billing." path="/account" noindex />
      <span className="eyebrow">Settings</span>
      <h1 className="font-body font-extrabold text-navy text-[26px] sm:text-[32px] mt-1 mb-6">Account</h1>

      <form onSubmit={save} className="card-mtr space-y-4" data-testid="profile-form">
        <h2 className="font-bold text-navy text-[18px]">Profile</h2>
        <div>
          <label htmlFor="name" className="label-mtr">Full name</label>
          <div className="relative">
            <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
            <input id="name" className="input-mtr pl-11" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="account-name" />
          </div>
        </div>
        <div>
          <label htmlFor="company" className="label-mtr">Company</label>
          <div className="relative">
            <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
            <input id="company" className="input-mtr pl-11" value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} data-testid="account-company" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="phone" className="label-mtr">Phone <span className="text-mortar normal-case">(for reports)</span></label>
            <div className="relative">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
              <input id="phone" className="input-mtr pl-11" value={form.company_phone} onChange={(e) => setForm((f) => ({ ...f, company_phone: e.target.value }))} placeholder="(555) 123-4567" data-testid="account-phone" />
            </div>
          </div>
          <div>
            <label htmlFor="license" className="label-mtr">License # <span className="text-mortar normal-case">(for reports)</span></label>
            <div className="relative">
              <Hash size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
              <input id="license" className="input-mtr pl-11" value={form.license_number} onChange={(e) => setForm((f) => ({ ...f, license_number: e.target.value }))} placeholder="ABC-12345" data-testid="account-license" />
            </div>
          </div>
        </div>
        <div>
          <label className="label-mtr">Email</label>
          <div className="relative">
            <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-mortar" aria-hidden="true" />
            <input className="input-mtr pl-11 bg-fieldstone cursor-not-allowed" value={user.email} disabled data-testid="account-email" />
          </div>
          <p className="text-[12px] text-mortar mt-1.5 flex items-center gap-1.5">
            <ShieldCheck size={13} /> Signed in via {user.auth_provider === "google" ? "Google" : "email & password"}.
          </p>
        </div>
        <button type="submit" disabled={saving} className="btn-primary" data-testid="account-save">
          {saving ? <Loader2 className="animate-spin" size={18} /> : "Save changes"}
        </button>
      </form>

      <div className="card-mtr mt-5" data-testid="branding-card">
        <h2 className="font-bold text-navy text-[18px] flex items-center gap-2"><ImageIcon size={20} className="text-mortar" /> Report branding</h2>
        <p className="text-mortar text-[14px] mt-2 mb-4">Upload your company logo to appear on the PDF reports you share with customers and insurance adjusters.</p>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-[140px] h-[72px] rounded-input border-2 border-gravel bg-fieldstone grid place-items-center overflow-hidden shrink-0" data-testid="logo-preview">
            {hasLogo ? (
              <img src={`${API}/account/logo?v=${logoVersion}`} alt="Company logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-mortar text-[12px] flex flex-col items-center gap-1"><ImageIcon size={20} /> No logo</span>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onLogoFile} data-testid="logo-file-input" />
            <button type="button" disabled={logoBusy} onClick={() => fileRef.current?.click()} className="btn-secondary !min-h-[40px] !py-2" data-testid="logo-upload-btn">
              {logoBusy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} {hasLogo ? "Replace logo" : "Upload logo"}
            </button>
            {hasLogo && (
              <button type="button" disabled={logoBusy} onClick={removeLogo} className="btn-ghost !min-h-[40px] !py-2" data-testid="logo-remove-btn">
                <Trash2 size={16} /> Remove
              </button>
            )}
          </div>
        </div>
        <p className="text-[12px] text-mortar mt-3">PNG, JPG or WebP up to 3 MB. Transparent PNG looks best.</p>

        <PdfBrandingPreview form={form} hasLogo={hasLogo} logoVersion={logoVersion} userName={user.name} />
      </div>

      <div className="card-mtr mt-5">
        <p className="text-mortar text-[14px] mt-2">
          You pay securely per report at checkout via Stripe — there's nothing to set up here. Returning
          customers can reuse a saved card on Stripe's secure payment page, and receipts are emailed automatically.
        </p>
      </div>

      <div className="mt-5">
        <BillingHistory />
      </div>

      <div className="card-mtr mt-5">
        <h2 className="font-bold text-navy text-[18px]">Session</h2>
        <p className="text-mortar text-[14px] mt-2 mb-4">Sign out of MeasureTheRoof on this device.</p>
        <button onClick={doLogout} className="btn-ghost" data-testid="account-logout"><LogOut size={18} /> Log out</button>
      </div>
    </div>
  );
}
